
import React from 'react';
import { BadgeDefinition, RewardItem, UserProfile } from '../../types';

const BADGES: BadgeDefinition[] = [
    // --- PERFORMANCE ---
    { id: 'metronome_killer', label: 'Metronome Killer', description: 'Atteindre 95% de calage sur 5 sessions.', icon: '⏱️', rarity: 'RARE', requirement: '5 Sessions > 95% Calage', category: 'TIMING' },
    { id: 'perfect_pitch', label: 'Perfect Pitch', description: 'Aucune erreur harmonique détectée.', icon: '🎵', rarity: 'EPIC', requirement: '100% Harmonie sur un morceau', category: 'HARMONY' },
    
    // --- PROGRESSION (NOUVEAU) ---
    { id: 'fast_learner', label: 'Fast Learner', description: 'Améliorer son score de +15% sur un morceau en 24h.', icon: '📈', rarity: 'RARE', requirement: '+15% Gain Score / 24h', category: 'GRIND' },
    { id: 'unstoppable', label: 'Unstoppable', description: 'Amélioration constante sur 7 jours.', icon: '🚀', rarity: 'EPIC', requirement: '7 jours de progression continue', category: 'GRIND' },

    // --- REGULARITÉ (NOUVEAU) ---
    { id: 'studio_addict', label: 'Studio Addict', description: 'Se connecter 5 jours consécutifs.', icon: '🔥', rarity: 'COMMON', requirement: '5 jours de streak', category: 'GRIND' },
    { id: 'night_owl', label: 'Night Owl', description: 'S’entraîner après minuit.', icon: '🦉', rarity: 'COMMON', requirement: '3 Sessions de nuit', category: 'GRIND' },
    
    // --- SOCIAL & ARENA (NOUVEAU) ---
    { id: 'matchmaker', label: 'The Matchmaker', description: 'Envoyer 10 invitations de duels Arena.', icon: '⚔️', rarity: 'RARE', requirement: '10 Invitations envoyées', category: 'SOCIAL' },
    { id: 'first_responder', label: 'First Responder', description: 'Accepter un défi en moins de 5 minutes.', icon: '⚡', rarity: 'EPIC', requirement: 'Réponse rapide Arena', category: 'SOCIAL' },
    { id: 'community_hero', label: 'Community Hero', description: 'Participer à 50 matchs Arena.', icon: '🦸', rarity: 'LEGENDARY', requirement: '50 Matchs joués', category: 'SOCIAL' },

    // --- TECHNIQUE ---
    { id: 'silence_pro', label: 'Shadow Player', description: 'Zéro bruit parasite détecté.', icon: '🔇', rarity: 'RARE', requirement: '90% Propreté sur 3 sessions', category: 'TIMING' },
];

const REWARDS: RewardItem[] = [
    { id: 'frame_gold', label: 'Golden Frame', type: 'AVATAR_FRAME', unlockLevel: 5, icon: '🖼️' },
    { id: 'theme_neon', label: 'Neon UI Skin', type: 'UI_THEME', unlockLevel: 10, icon: '🎨' },
    { id: 'secret_backing', label: 'Blues Master Track', type: 'SECRET_TRACK', unlockLevel: 3, icon: '🎼' },
    { id: 'metronome_vintage', label: 'Analog Clicks', type: 'SOUND_PACK', unlockLevel: 7, icon: '🔊' },
];

interface RewardGalleryProps {
    profile: UserProfile;
}

export const RewardGallery: React.FC<RewardGalleryProps> = ({ profile }) => {
    const isUnlocked = (badgeId: string) => profile.badges.includes(badgeId);
    const userLevel = Math.floor(profile.stats.levelXP / 1000) + 1;

    const getRarityStyles = (rarity: string, unlocked: boolean) => {
        if (!unlocked) return 'border-white/5 bg-black/40 text-slate-600 grayscale';
        
        switch(rarity) {
            case 'COMMON': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.2)]';
            case 'RARE': return 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300 shadow-[0_0_25px_rgba(6,182,212,0.3)]';
            case 'EPIC': return 'border-purple-500/50 bg-purple-500/20 text-purple-200 shadow-[0_0_30px_rgba(168,85,247,0.4)]';
            case 'LEGENDARY': return 'border-smart-gold bg-smart-gold/20 text-white shadow-[0_0_40px_rgba(218,144,0,0.5)] animate-pulse';
            default: return 'border-white/10 bg-white/5 text-white';
        }
    };

    const getCategoryIcon = (cat: string) => {
        switch(cat) {
            case 'TIMING': return '⏱️';
            case 'HARMONY': return '🎵';
            case 'SOCIAL': return '⚔️';
            case 'GRIND': return '🔥';
            default: return '🎸';
        }
    };

    return (
        <div className="space-y-16 animate-fade-in pb-32">
            {/* BADGES SECTION */}
            <section className="space-y-10">
                <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-4 px-4">
                    <div className="space-y-1 text-center md:text-left">
                        <h3 className="text-xl md:text-2xl font-black text-white uppercase italic tracking-tighter">Tableau d'Honneur</h3>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.4em]">Le prestige se gagne par la sueur et le partage</p>
                    </div>
                    <div className="bg-white/5 px-6 py-2 rounded-full border border-white/10 flex items-center gap-4">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progression Collection</span>
                        <div className="w-32 h-1.5 bg-black/40 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-smart-gold transition-all duration-1000 shadow-[0_0_10px_#DA9000]" 
                                style={{ width: `${(profile.badges.length / BADGES.length) * 100}%` }} 
                            />
                        </div>
                        <span className="text-[10px] font-mono text-white">{profile.badges.length}/{BADGES.length}</span>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {BADGES.map(badge => {
                        const unlocked = isUnlocked(badge.id);
                        const styles = getRarityStyles(badge.rarity, unlocked);
                        
                        return (
                            <div key={badge.id} className={`p-8 rounded-[40px] border transition-all duration-500 relative overflow-hidden group ${styles} ${unlocked ? 'hover:scale-[1.02]' : 'opacity-60 hover:opacity-80'}`}>
                                {unlocked && (
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none opacity-50"></div>
                                )}
                                
                                <div className="flex items-start gap-8 relative z-10">
                                    <div className={`w-20 h-20 rounded-3xl flex items-center justify-center text-4xl transition-all duration-700 shadow-2xl relative ${unlocked ? 'bg-white/10 rotate-3 group-hover:rotate-0 scale-110' : 'bg-black/60 scale-90'}`}>
                                        {unlocked ? badge.icon : '🔒'}
                                        {unlocked && <div className="absolute inset-0 bg-white/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                                    </div>
                                    
                                    <div className="flex-1 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h4 className={`text-lg font-black uppercase tracking-tighter italic leading-none ${unlocked ? 'text-white' : 'text-slate-500'}`}>
                                                    {badge.label}
                                                </h4>
                                                <span className="text-[8px] font-black uppercase tracking-widest opacity-50 mt-1 block">
                                                    {getCategoryIcon(badge.category)} {badge.category}
                                                </span>
                                            </div>
                                            <div className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${unlocked ? 'border-current' : 'border-slate-800 text-slate-700'}`}>
                                                {badge.rarity}
                                            </div>
                                        </div>
                                        
                                        <p className="text-sm font-medium text-slate-400 italic leading-snug">
                                            "{badge.description}"
                                        </p>

                                        {!unlocked && (
                                            <div className="pt-4 space-y-2">
                                                <div className="flex justify-between items-center text-[8px] font-black text-slate-600 uppercase tracking-widest">
                                                    <span>Progression</span>
                                                    <span>20%</span>
                                                </div>
                                                <div className="h-1 w-full bg-black/40 rounded-full overflow-hidden">
                                                    <div className="h-full bg-slate-800 w-[20%]"></div>
                                                </div>
                                                <span className="text-[9px] font-black text-smart-accent uppercase tracking-widest block pt-1">
                                                    🎯 {badge.requirement}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {unlocked && (
                                            <div className="pt-2 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Obtenu !</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* GEAR & GOODIES SECTION */}
            <section className="space-y-10">
                 <div className="px-4">
                    <h3 className="text-xl md:text-2xl font-black text-white uppercase italic tracking-tighter">Loot & Gear Shop</h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.4em] mt-1">Débloquez des skins et du contenu exclusif</p>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {REWARDS.map(reward => {
                        const unlocked = userLevel >= reward.unlockLevel;
                        return (
                            <div key={reward.id} className={`group p-8 rounded-[48px] border flex flex-col items-center text-center gap-6 transition-all duration-500 relative overflow-hidden ${unlocked ? 'bg-gradient-to-b from-slate-900 to-black border-emerald-500/20 shadow-[0_20px_40px_rgba(0,0,0,0.5)]' : 'bg-black/40 border-white/5 opacity-40 grayscale'}`}>
                                {unlocked && <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-emerald-500/5 to-transparent pointer-events-none"></div>}
                                
                                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-5xl mb-2 transition-transform duration-700 group-hover:scale-110 ${unlocked ? 'bg-white/5 shadow-[0_0_30px_rgba(255,255,255,0.05)] border border-white/10' : 'bg-black/60'}`}>
                                    {unlocked ? reward.icon : '🎁'}
                                </div>
                                
                                <div className="space-y-1">
                                    <h5 className="text-sm font-black text-white uppercase tracking-tight">{reward.label}</h5>
                                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">{reward.type.replace('_', ' ')}</span>
                                </div>
                                
                                {!unlocked ? (
                                    <div className="mt-4 px-6 py-2 bg-white/5 rounded-2xl text-[9px] font-black text-slate-500 uppercase tracking-widest border border-white/5">
                                        Niveau {reward.unlockLevel} requis
                                    </div>
                                ) : (
                                    <button className="mt-4 w-full py-3 bg-emerald-500 text-black rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white hover:scale-105 transition-all shadow-xl">
                                        ÉQUIPER ⚡
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};
